import Select from "@/components/Select";
import { TypedDocumentNode, gql, useQuery } from "@apollo/client";
import { useFormContext, Controller } from "react-hook-form";

type ChapterAutocompleteResult = {
  id: string;
  name: string;
  code: string;
};

export const SECTION_CHAPTER_OPTIONS_QUERY: TypedDocumentNode<
  {
    section: {
      codedChapters: ChapterAutocompleteResult[];
    } | null;
  },
  { sectionId: string }
> = gql`
  query SectionChapterOptions($sectionId: ID!) {
    section(id: $sectionId) {
      codedChapters {
        id
        name
        code
      }
    }
  }
`;

const NONE_OPTION_VALUE = "__none__";

function ChapterAutocomplete({
  name,
  label,
  sectionId,
  initialChapter
}: {
  name: string;
  label: string;
  sectionId?: string;
  initialChapter?: { id: string; name: string } | null;
}) {
  const {
    control,
    formState: { errors, isSubmitting }
  } = useFormContext();
  const { data, loading } = useQuery(SECTION_CHAPTER_OPTIONS_QUERY, {
    variables: { sectionId: sectionId || "" },
    skip: !sectionId
  });
  const fetchedChapterOptions = (data?.section?.codedChapters || []).map((chapter) => ({
    value: chapter.id,
    label: chapter.name
  }));
  const includesInitialChapter =
    !!initialChapter && fetchedChapterOptions.some((option) => option.value === initialChapter.id);
  const chapterOptions = sectionId
    ? [
        { value: NONE_OPTION_VALUE, label: "None" },
        ...fetchedChapterOptions,
        ...(!includesInitialChapter && initialChapter ? [{ value: initialChapter.id, label: initialChapter.name }] : [])
      ]
    : [];

  const supportingText = !sectionId ? "Select a section first." : undefined;
  const placeholder = sectionId && loading ? "Loading chapters..." : undefined;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <Select
          label={label}
          options={chapterOptions}
          value={value || (sectionId ? NONE_OPTION_VALUE : "")}
          onSelect={(selectedValue) => onChange(selectedValue === NONE_OPTION_VALUE ? "" : selectedValue)}
          disabled={isSubmitting || !sectionId || loading}
          placeholder={placeholder}
          supportingText={supportingText}
          error={!!errors[name]}
          errorText={errors[name]?.message as string}
          data-testid="chapter-select"
        />
      )}
    />
  );
}

export default ChapterAutocomplete;
